import React, { useState } from 'react'
import type { ComponentEntry } from '../types'
import type { ComponentGroup } from './component-groups.config'
import { wideComponents } from './component-groups.config'
import { ComponentCard } from './ComponentCard'
import { ChevronDown, ChevronRight } from 'lucide-react'

export interface ResolvedCluster {
  label: string
  components: ComponentEntry[]
}

interface GroupSectionProps {
  group: ComponentGroup
  components: ComponentEntry[]
  clusters: ResolvedCluster[]
  getPreviewer: (entry: ComponentEntry) => React.ReactNode | undefined
}

export function GroupSection({
  group,
  components,
  clusters,
  getPreviewer,
}: GroupSectionProps): React.ReactElement {
  const [open, setOpen] = useState(true)

  const totalCount =
    components.length +
    clusters.reduce((sum, c) => sum + c.components.length, 0)
  if (totalCount === 0) return <></>

  return (
    <section>
      <button
        type="button"
        className="flex items-center gap-3 w-full text-left py-3 px-1 hover:bg-surface-hover rounded-md"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="font-semibold text-base text-fg">{group.label}</span>
        <span className="text-fg-secondary text-sm">{group.description}</span>
        <span className="ml-auto text-xs bg-surface-alt text-fg-secondary px-2 py-0.5 rounded-full">
          {totalCount}
        </span>
      </button>

      {open && (
        <div className="flex flex-col gap-4 mt-2 pb-8">
          {/* Ungrouped components */}
          {components.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {components.map((entry) => (
                <ComponentCard
                  key={entry.path}
                  entry={entry}
                  preview={getPreviewer(entry)}
                  wide={wideComponents.has(entry.name)}
                />
              ))}
            </div>
          )}

          {/* Clusters */}
          {clusters.map((cluster) => (
            <div
              key={cluster.label}
              className="border border-dashed border-line rounded-lg p-4"
            >
              <span className="text-xs font-medium text-fg-secondary px-2 py-0.5 rounded border border-line bg-surface inline-block mb-3">
                {cluster.label}
              </span>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {cluster.components.map((entry) => (
                  <ComponentCard
                    key={entry.path}
                    entry={entry}
                    preview={getPreviewer(entry)}
                    wide={wideComponents.has(entry.name)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
